/**
 * Audit Log phase — admin-only table of security-relevant events for
 * the caller's company. Events are written server-side at mutation
 * points (auth.login, brand.updated, member.invited, …) via the
 * db/auditLog.js helper; this component is the read side.
 *
 * Pagination: cursor-based. Each fetch returns up to `limit` rows
 * ordered by id DESC. "Load more" fetches using beforeId = last row id.
 *
 * Filtering:
 *   - Event group — a prefix like 'member.', 'brand.', etc. Kept as
 *     prefixes so adding a new specific event type doesn't force a
 *     frontend change (the UI filters by group).
 *
 * Non-admins: the backend returns 403, which the frontend surfaces as
 * a friendly "admin only" panel instead of a raw error.
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AuditLog.css';

// Human-readable labels for every event type. Keep in sync with
// backend/db/auditLog.js EVENT_TYPES. Unknown types fall back to
// the raw `event_type` string so a backend-added event isn't invisible
// in the UI before the frontend catches up.
const EVENT_LABELS = {
  'auth.login': 'Signed in',
  'auth.login_failed': 'Failed sign-in attempt',
  'auth.logout': 'Signed out',
  'auth.password_reset': 'Password reset',
  'member.invited': 'Invited a teammate',
  'member.invite_revoked': 'Revoked a pending invite',
  'member.joined': 'Teammate joined',
  'member.removed': 'Removed a teammate',
  'member.role_changed': 'Changed a teammate\'s role',
  'user.deleted': 'Deleted a user account',
  'brand.updated': 'Updated brand',
  'ai.personalization_updated': 'Updated AI personalization',
  'bot.display_name_updated': 'Changed bot display name',
  'consent.jurisdiction_updated': 'Updated consent jurisdiction',
  'consent.acknowledged': 'Acknowledged recording consent',
  'demo.deleted': 'Deleted a demo',
  'demo.uploaded': 'Uploaded a demo',
  'trailer.built': 'Built a trailer',
  'share.created': 'Created a share link',
  'share.revoked': 'Revoked a share link',
  'doc.downloaded': 'Downloaded a document',
  'space.created': 'Created a Decision Room',
  'space.deleted': 'Deleted a Decision Room',
  'space.transferred_ownership': 'Transferred Decision Room ownership',
  'space.collaborator_added': 'Added a Decision Room collaborator',
  'space.collaborator_removed': 'Removed a Decision Room collaborator',
  'admin.cross_rep_read': 'Admin viewed another rep\'s Decision Room',
  'admin.cross_rep_write': 'Admin edited another rep\'s Decision Room',
  'bot.scheduled': 'Scheduled a meeting bot',
  'calendar.connected': 'Connected a calendar',
  'calendar.disconnected': 'Disconnected a calendar',
  'calendar.auto_schedule_toggled': 'Toggled auto-schedule',
};

// Filter groups — prefix-based so all `member.*` events aggregate
// under "Team".
const FILTER_GROUPS = [
  { id: 'all',     label: 'All events',   prefix: null },
  { id: 'team',    label: 'Team',         prefix: null,
                   prefixes: ['member.', 'user.'] },
  { id: 'auth',    label: 'Auth',         prefix: 'auth.' },
  { id: 'config',  label: 'Config',       prefix: null, // handled specially
                   prefixes: ['brand.', 'ai.', 'bot.', 'consent.'] },
  { id: 'bots',    label: 'Bots & calendar', prefix: null,
                   prefixes: ['bot.', 'calendar.'] },
  { id: 'sharing', label: 'Sharing',      prefix: null,
                   prefixes: ['share.', 'doc.'] },
  { id: 'demos',   label: 'Demos',        prefix: 'demo.' },
  // Decision Rooms + privileged-access events get their own group so
  // admins reviewing the audit log can scope to deal-room activity
  // (and so cross-rep-access events surface clearly).
  { id: 'spaces',  label: 'Decision Rooms', prefix: null,
                   prefixes: ['space.', 'admin.'] },
];

// djb2 → deterministic color per actor so the table is scannable
// when one admin makes many changes in a row.
function actorColor(email) {
  if (!email) return 'rgba(148, 163, 184, 0.22)';
  let h = 5381;
  for (let i = 0; i < email.length; i += 1) {
    h = ((h << 5) + h + email.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(h) % 360;
  return `hsla(${hue}, 50%, 65%, 0.22)`;
}

function initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+|@|\./).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function formatTs(iso) {
  const d = new Date(iso);
  const now = Date.now();
  const diff = Math.floor((now - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  // Older than 7 days — show the date.
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AuditLog({ apiBase, authToken }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchPage = useCallback(async ({ reset, beforeId }) => {
    setLoading(true);
    setError(null);
    try {
      const group = FILTER_GROUPS.find((g) => g.id === filter);
      // Multi-prefix groups (Config, Bots) pull each prefix and merge.
      const prefixes = group?.prefixes || (group?.prefix ? [group.prefix] : [null]);
      const perPrefix = Math.ceil(50 / prefixes.length);
      const pages = await Promise.all(prefixes.map(async (prefix) => {
        const q = new URLSearchParams({ limit: String(perPrefix) });
        if (prefix) q.set('eventPrefix', prefix);
        if (beforeId) q.set('beforeId', String(beforeId));
        const res = await fetch(`${apiBase}/api/audit-log?${q.toString()}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.status === 403) {
          setForbidden(true);
          return { events: [] };
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }));
      const merged = pages.flatMap((p) => p.events || []);
      // Dedupe (multi-prefix fetches can overlap at the margins — shouldn't
      // but defense-in-depth) + stable sort by id DESC.
      const seen = new Set();
      const uniq = [];
      for (const e of merged) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        uniq.push(e);
      }
      uniq.sort((a, b) => b.id - a.id);
      setEvents((prev) => (reset ? uniq : [...prev, ...uniq]));
      setHasMore(uniq.length >= 50);
    } catch (e) {
      setError(e.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken, filter]);

  // Initial + filter-change fetch.
  useEffect(() => {
    setForbidden(false);
    fetchPage({ reset: true });
  }, [fetchPage]);

  const filtered = events.filter((e) => {
    if (!search) return true;
    const hay = `${e.actorEmail || ''} ${e.actorName || ''} ${e.summary || ''} ${e.eventType}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  if (forbidden) {
    return (
      <div className="audit-log-forbidden">
        <h3>Admins only</h3>
        <p>
          The audit log shows security-relevant actions across your
          workspace. Ask an admin to grant you admin access, or request
          a one-time export if you need it for compliance.
        </p>
      </div>
    );
  }

  return (
    <div className="audit-log">
      <div className="audit-log-controls">
        <div className="audit-log-filters" role="tablist">
          {FILTER_GROUPS.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={filter === g.id}
              className={`audit-log-filter ${filter === g.id ? 'is-active' : ''}`}
              onClick={() => setFilter(g.id)}
            >
              {g.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="audit-log-search"
          placeholder="Search actor, event, summary…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* Compliance export (2026-07-26): newest 5000 events, honoring
            the active group filter. Multi-prefix groups export their
            first prefix; "All" exports everything. */}
        <button
          type="button"
          onClick={async () => {
            try {
              const group = FILTER_GROUPS.find((g) => g.id === filter);
              const prefix = group?.prefix || (group?.prefixes && group.prefixes[0]) || null;
              const q = prefix ? `?eventPrefix=${encodeURIComponent(prefix)}` : '';
              const res = await fetch(`${apiBase}/api/audit-log/export.csv${q}`, {
                headers: { Authorization: `Bearer ${authToken}` },
              });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const blob = await res.blob();
              const href = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = href;
              a.download = 'trailercast-audit-log.csv';
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(href);
            } catch (e) {
              setError(`Export failed: ${e.message}`);
            }
          }}
          title="Download the newest 5000 events as CSV"
          style={{
            flex: 'none', padding: '8px 12px', borderRadius: 8,
            border: '1px solid var(--room-border, #e2e8f0)', background: 'transparent',
            color: 'var(--room-text-muted, #64748b)', fontSize: 12, fontWeight: 600,
            fontFamily: 'inherit', cursor: 'pointer',
          }}
        >
          ⤓ Export CSV
        </button>
      </div>

      {error && <div className="audit-log-error">{error}</div>}

      {loading && events.length === 0 && (
        <div className="audit-log-empty">Loading…</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="audit-log-empty">
          {events.length === 0
            ? 'No events logged yet. Actions from the last 90 days will appear here.'
            : 'No events match your search.'}
        </div>
      )}

      {filtered.length > 0 && (
        <ul className="audit-log-list">
          {filtered.map((e) => {
            const actorLabel = e.actorName || e.actorEmail || 'System';
            const eventLabel = EVENT_LABELS[e.eventType] || e.eventType;
            return (
              <li key={e.id} className="audit-log-row">
                <div
                  className="audit-log-actor-avatar"
                  style={{ background: actorColor(e.actorEmail) }}
                  title={actorLabel}
                >
                  {initials(e.actorName, e.actorEmail)}
                </div>
                <div className="audit-log-row-main">
                  <div className="audit-log-row-head">
                    <span className="audit-log-actor">{actorLabel}</span>
                    <span className="audit-log-event-pill">{eventLabel}</span>
                    <span className="audit-log-ts" title={new Date(e.createdAt).toLocaleString()}>
                      {formatTs(e.createdAt)}
                    </span>
                  </div>
                  {e.summary && (
                    <div className="audit-log-summary">{e.summary}</div>
                  )}
                  <div className="audit-log-meta">
                    {e.actorEmail && <span>{e.actorEmail}</span>}
                    {e.ipAddress && <span>· {e.ipAddress}</span>}
                    {e.subjectType && e.subjectId && (
                      <span>· {e.subjectType} #{e.subjectId}</span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && events.length > 0 && (
        <button
          type="button"
          className="audit-log-load-more"
          disabled={loading}
          onClick={() => {
            const lastId = events[events.length - 1]?.id;
            if (lastId) fetchPage({ reset: false, beforeId: lastId });
          }}
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}
